import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";

const BrandLogo = ({ name, domain }) => {
  const [failed, setFailed] = useState(false);
  if (failed || !domain) {
    return <span className="font-serif text-2xl sm:text-3xl text-ink3 opacity-70 whitespace-nowrap">{name}</span>;
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=128`}
      alt={name}
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-7 sm:h-9 w-auto max-w-[140px] object-contain grayscale opacity-50 hover:opacity-90 transition-opacity duration-300"
    />
  );
};

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.6,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref} className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-ink">
      {display}
      {suffix && <span className="text-brass">{suffix}</span>}
    </span>
  );
};

export const StatsStrip = ({ t }) => (
  <section data-testid="stats-strip" className="py-20 sm:py-24 border-b border-line">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-8">
        {t.stats.items.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`stat-item-${i}`}
            className="border-l border-line pl-6"
          >
            <Counter value={s.value} suffix={s.suffix} />
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink3 mt-3">{s.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-16 sm:mt-20">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink3 mb-7 text-center">
          {t.stats.title}
        </p>
        <div data-testid="brands-frise" className="overflow-hidden select-none" aria-hidden="true">
          <div className="marquee-track flex items-center gap-16 sm:gap-20 w-max">
            {[0, 1].map((i) => (
              <div key={i} className="flex items-center gap-16 sm:gap-20 pr-16 sm:pr-20">
                {t.stats.brands.concat(t.stats.brands).map((b, j) => (
                  <BrandLogo key={`${b.name}-${j}`} name={b.name} domain={b.domain} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);
